import { Injectable, NgModule } from '@angular/core';
import { Router } from '@angular/router';
import {
  AlertController,
  LoadingController,
  MenuController,
  ModalController,
  Platform,
  PopoverController,
  ToastController,
} from '@ionic/angular';
import { AuthenticationService } from './authentication.service';
import { NotAllowedPage } from 'src/app/shared/modals/not-allowed/not-allowed.page';
import { PrinterOptionsComponent } from 'src/app/shared/_components/printer-options/printer-options.component';
import { ExternalAppComponent } from '../_components/external-app/external-app.component';
import { WithdrawTooltipComponent } from '../_components/withdraw-tooltip/withdraw-tooltip.component';
import { CloseOverlayService } from './closeOverlay.service';
import { Location } from '@angular/common';
import { Market } from '@ionic-native/market/ngx';
import { LoginStore, WithdrawStore } from './store';
import { UpgradeStore } from './store/upgradeStore.service';

@NgModule({
  providers: [ExternalAppComponent, Market],
})
@Injectable({ providedIn: 'root' })
export class AlertService {
  constructor(
    private router: Router,
    private market: Market,
    private location: Location,
    private platform: Platform,
    private menu: MenuController,
    private auth: AuthenticationService,
    private loginStore: LoginStore,
    private upgradeStore: UpgradeStore,
    private withdrawStore: WithdrawStore,
    private alertController: AlertController,
    private modalController: ModalController,
    private toastController: ToastController,
    private externalApp: ExternalAppComponent,
    private loadingController: LoadingController,
    private popoverController: PopoverController,
    private closeOverlayService: CloseOverlayService
  ) {}

  subscription: any;

  async loading(message: any) {
    const loading = await this.loadingController.create({
      message,
      spinner: 'lines',
      cssClass: 'ion-text-center loading',
    });
    await loading.present();

    const stopLoading = await loading.onDidDismiss();
  }

  async standardAlert(message: any) {
    const loading = await this.loadingController.create({
      message,
      duration: 6000,
      spinner: null,
      backdropDismiss: true,
      cssClass: 'ion-text-center loading',
    });
    await loading.present();
  }

  async standardAlertNullDuration(message: any) {
    const loading = await this.loadingController.create({
      message,
      duration: null,
      spinner: null,
      backdropDismiss: true,
      cssClass: 'ion-text-center loading',
    });
    await loading.present();
    this.alertController.getTop().then((res) => {
      console.log(res);
    });
  }

  async retry(message: any, duration: any, backdropDismiss: boolean) {
    const loading = await this.loadingController.create({
      message,
      duration,
      spinner: null,
      backdropDismiss,
      cssClass: 'ion-text-center loading',
    });
    await loading.present();
  }

  async callToAction(header: any, message: any, text: any) {
    const alert = await this.alertController.create({
      header,
      message,
      backdropDismiss: true,
      buttons: [
        {
          text,
          cssClass: 'mg-auto alert-button-md alert-button-ios',
          role: 'cancel',
          handler: () => {
            if (this.router.url === '/login') {
              this.router.navigateByUrl('/verify-email');
            }
            if (this.platform.is('cordova')) {
              if (
                this.router.url === '/forgot-password' ||
                this.router.url === '/verify-email'
              ) {
                this.externalApp.launchApp();
              }
            } else {
              this.router.navigateByUrl('/login');
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async standardAlertWithButton(
    header: any,
    message: any,
    backdropDismiss: boolean,
    text: any
  ) {
    const alert = await this.alertController.create({
      header,
      message,
      backdropDismiss,
      buttons: [
        {
          text,
          cssClass: 'mg-auto fs-18 text-capitalize text-primary',
          role: 'destructive',
          handler: ($event) => {
            console.log($event);
            if (this.router.url === '/cashiers') {
              this.alertController.dismiss();
            } else if (text === 'Login') {
              this.router.navigateByUrl('/login');
            } else if (text.includes('Let\'s')) {
              if (this.platform.is('android')) {
                this.market.open('com.parkway.bank3d.vaps');
              } else if (this.platform.is('ios')) {
                this.market.open('com.parkway.bank3d');
              }
            } else if (this.router.url.includes('upgrade-account') && text === 'Got it') {
              this.upgradeStore.setIsUpgradeInfoDismissed(true);
            } else if (text === 'Go to Dashboard')  {
              this.router.navigateByUrl('dashboard');
            } else {
              this.alertController.dismiss();
            }
          },
        },
      ],
    });
    await alert.present();
  }

  // Menu
  async openSideMenu() {
    this.menu.enable(true, 'sideMenu');
    await this.menu.open('sideMenu');
  }

  async openAllCashiers() {
    if (this.auth.currentUserValue.role === 'merchant') {
      this.menu.enable(true, 'allCashiers');
      await this.menu.open('allCashiers');
    } else {
      this.notAllowed();
    }
  }

  async newPayment() {
    this.closeMenu();
    this.router.navigate(['/new-payment'], { queryParams: { new: true } });
  }

  closeMenu() {
    this.menu.close();
  }

  // Modal for cashiers/non-admin
  async notAllowed() {
    const modal = await this.modalController.create({
      component: NotAllowedPage,
      cssClass: 'bottom-modal h-50',
      backdropDismiss: true,
      swipeToClose: true,
    });
    return await modal.present();
  }

  // Toast for actions
  async standardToast(message: string, position: any) {
    const toast = await this.toastController.create({
      message,
      color: 'light',
      mode: 'ios',
      duration: 2000,
      cssClass: 'bottom-toast',
      position,
    });
    toast.present();
  }

  async standardToastWithOptions(header: any, message: any) {
    const toast = await this.toastController.create({
      header,
      message,
      position: 'top',
      mode: 'ios',
      color: 'light',
      duration: 7000,
      cssClass: 'cashier-toast checkmark',
      buttons: [
        {
          side: 'start',
          icon: 'checkmark-outline',
          cssClass: 'checkmark',
        },
        {
          icon: 'close',
          role: 'cancel',
          cssClass: 'close-toast',
          handler: () => {
            if (this.router.url === '/cashiers') {
              console.log('Cancelled');
            } else if (this.router.url === '/withdraw') {
              this.router.navigateByUrl('/dashboard');
            }
          },
        },
      ],
    });
    toast.present();
  }

  // Printer Options
  async showOptions(event: any) {
    const popover = await this.popoverController.create({
      component: PrinterOptionsComponent,
      cssClass: 'printer-pd',
      event,
      translucent: true,
      showBackdrop: false,
    });
    return await popover.present();
  }

  // Withdraw Tooltip
  async showWithdrawTooltip(event: any) {
    const popover = await this.popoverController.create({
      component: WithdrawTooltipComponent,
      cssClass: 'withdraw-tooltip tooltip-animate tooltip-animate-2',
      event,
      translucent: true,
      backdropDismiss: true,
      mode: 'ios',
    });
    await popover.present();
    this.popoverController.getTop().then((res) => {
      res.addEventListener('ionPopoverDidDismiss', () => {
        this.withdrawStore.setIsWithdrawTooltipTapped(true);
      });
    });
  }

  // Resfresh Tootlip
  async showRefreshTooltip() {
    const popover = await this.popoverController.create({
      component: WithdrawTooltipComponent,
      cssClass: 'withdraw-tooltip tooltip-animate tooltip-animate-2',
      translucent: true,
      backdropDismiss: true,
      mode: 'ios',
    });
    await popover.present();
    this.popoverController.getTop().then((res) => {
      res.addEventListener('ionPopoverDidDismiss', () => {
        this.withdrawStore.setIsRefreshTooltipTapped(true);
      });
    });
  }

  async checkForAlert() {
    try {
      const alert = await this.alertController.getTop();
      const loading = await this.loadingController.getTop();
      if (alert) {
        alert.dismiss();
        return;
      } else if (loading) {
        loading.dismiss();
        return;
      } else {
        if (
          this.router.url === '/confirm-payment' ||
          this.router.url === '/demo/confirm'
        ) {
          this.cancelTransactionAlert();
        } else if (
          this.router.url === '/cashiers' ||
          this.router.url === '/withdraw'
        ) {
          this.router.navigateByUrl('/dashboard');
        } else {
          if (this.platform.is('cordova')) {
            this.exitAlert();
          }
        }
      }
    } catch (error) {}
  }

  async cancelTransactionAlert() {
    const alert = await this.alertController.create({
      header: 'Cancel Transaction',
      message: '<strong>Are you sure you want to leave?</strong>',
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Yes',
          cssClass: 'text-danger',
          handler: () => {
            if (this.router.url === '/demo/confirm') {
              this.router.navigateByUrl('/login');
              document.querySelector('app-confirm').remove();
            } else {
              this.router.navigateByUrl('/dashboard');
              document.querySelector('app-confirm-payment').remove();
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async exitAlert() {
    const alert = await this.alertController.create({
      header: 'Exit',
      message: '<strong>Are you sure you want to exit the app?</strong>',
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Yes',
          cssClass: 'text-danger',
          handler: () => {
            console.log('User closed the app');
            const isApp = 'app';
            navigator[isApp].exitApp();
            if (this.loginStore.getIsRememberMeChecked() === 'false') {
              this.auth.logout();
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async logoutAlert() {
    this.menu.close();
    const alert = await this.alertController.create({
      header: 'Logout',
      message: '<strong>Are you sure you want to logout</strong>',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            console.log('Cancelled');
            this.router.navigate(['/dashboard']);
          },
        },
        {
          text: 'Logout',
          cssClass: 'text-danger',
          handler: () => {
            console.log('User logged out');
            this.auth.logout();
            this.router.navigate(['/login']);
          },
        },
      ],
    });

    await alert.present();
  }

  async loadingNullDuration(message: any) {
    const loading = await this.loadingController.create({
      message,
      duration: null,
      spinner: null,
      backdropDismiss: true,
      cssClass: 'ion-text-center br-5',
    });
    await loading.present();
  }

  async stopLoading() {
    return await this.loadingController.dismiss();
  }

  async dismissAlert() {
    return await this.alertController.dismiss();
  }

  async dismissModal() {
    return await this.modalController.dismiss();
  }

  closeModal(pageName: string) {
    this.subscription = this.platform.backButton.subscribeWithPriority(
      99999,
      () => {
        if (this.constructor.name === pageName) {
          this.closeOverlayService.trigger();
        } else if (this.router.url === '/new-payment') {
          this.router.navigateByUrl('/dashboard');
        } else {
          this.location.back();
        }
      }
    );
  }

  backButtonGoBack(url: string) {
    this.subscription = this.platform.backButton.subscribeWithPriority(
      99999,
      () => {
        this.router.navigateByUrl(url);
      }
    );
  }
}
